import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { toast } from "sonner";
import { roleKeys, roleLevelKeys } from "@/lib/query";
import type { CreateRoleLevelInput, UpdateRoleLevelInput } from "@/schemas/role-levels";

/** Everything that can reference a level. (Backend twin: api/role-levels.ts.) */
export interface RoleLevelUsage {
  roles: number;
  people: number;
  aspirations: number;
  expectations: number;
  reviews: number;
}

/** One career level in the catalogue. (Backend twin: api/role-levels.ts.) */
export interface RoleLevelRow {
  id: string;
  name: string;
  definition: string | null;
  sortOrder: number;
  retired: boolean;
  /** How many roles opt into this level — a level in use can't be retired. */
  roleCount: number;
  /**
   * References across all five tables. `usageTotal === 0` is the condition for
   * a hard delete, so the page can disable the action instead of offering one
   * the server will refuse.
   */
  usage: RoleLevelUsage;
  usageTotal: number;
}

async function readError(res: Response, fallback: string): Promise<never> {
  const body: unknown = await res.json().catch(() => null);
  const message =
    body !== null && typeof body === "object" && "error" in body && typeof body.error === "string"
      ? body.error
      : fallback;
  throw new Error(message);
}

/** The career-level catalogue in ladder order, retired levels included. */
export function useRoleLevels() {
  return useQuery({
    queryKey: roleLevelKeys.catalog,
    queryFn: async (): Promise<RoleLevelRow[]> => {
      const res = await fetch("/api/role-levels");
      if (!res.ok) throw new Error("Failed to fetch role levels");
      const json = await res.json();
      return json.data;
    },
  });
}

/** Append a level to the ladder. Admin-only server-side. */
export function useCreateRoleLevel() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateRoleLevelInput) => {
      const res = await fetch("/api/role-levels", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) return readError(res, "Failed to add level");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: roleLevelKeys.all });
      toast.success("Level added");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to add level"),
  });
}

/** Edit a level's name / definition. Admin-only server-side. */
export function useUpdateRoleLevel(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (input: UpdateRoleLevelInput) => {
      const res = await fetch(`/api/role-levels/${id}`, {
        method: "PATCH",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify(input),
      });
      if (!res.ok) return readError(res, "Failed to update level");
      return res.json();
    },
    onSuccess: () => {
      // Role detail embeds level names, so refresh roles too.
      qc.invalidateQueries({ queryKey: roleLevelKeys.all });
      qc.invalidateQueries({ queryKey: roleKeys.all });
      toast.success("Level updated");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to update level"),
  });
}

/** Renumber the ladder from a complete ordered id list. */
export function useReorderRoleLevels() {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (ids: string[]) => {
      const res = await fetch("/api/role-levels/reorder", {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ ids }),
      });
      if (!res.ok) return readError(res, "Failed to reorder levels");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: roleLevelKeys.all });
      qc.invalidateQueries({ queryKey: roleKeys.all });
    },
    onError: (e: Error) => toast.error(e.message || "Failed to reorder levels"),
  });
}

/**
 * Retire or restore a level. A level still applied to a role is refused by the
 * server with the blocking role count in the message — surface it verbatim
 * rather than inventing a friendlier lie.
 */
export function useSetRoleLevelRetired(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (retired: boolean) => {
      const res = await fetch(`/api/role-levels/${id}/retired`, {
        method: "PUT",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({ retired }),
      });
      if (!res.ok) return readError(res, "Failed to update level");
      return res.json();
    },
    onSuccess: (_data, retired) => {
      qc.invalidateQueries({ queryKey: roleLevelKeys.all });
      qc.invalidateQueries({ queryKey: roleKeys.all });
      toast.success(retired ? "Level retired" : "Level restored");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to update level"),
  });
}

/**
 * Delete a level outright. The server allows this only when nothing in the app
 * references it, and its refusal names what does — surface the message verbatim
 * rather than inventing a friendlier lie, same posture as the retire guard.
 *
 * Roles embed level names, and a delete nulls nobody's data (the guard makes
 * sure of that), but the role queries are invalidated anyway so a stale
 * catalogue can't linger in a role's level picker.
 */
export function useDeleteRoleLevel(id: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async () => {
      const res = await fetch(`/api/role-levels/${id}`, { method: "DELETE" });
      if (!res.ok) return readError(res, "Failed to delete level");
      return res.json();
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: roleLevelKeys.all });
      qc.invalidateQueries({ queryKey: roleKeys.all });
      toast.success("Level deleted");
    },
    onError: (e: Error) => toast.error(e.message || "Failed to delete level"),
  });
}
